import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import Button from './Button';
import api from '../utils/api';

const PollSettingsModal = ({ isOpen, onClose, sessionId }) => {
  const [language, setLanguage] = useState('en');
  const [gradientStart, setGradientStart] = useState('#667eea');
  const [gradientEnd, setGradientEnd] = useState('#764ba2');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Load settings when modal opens
  useEffect(() => {
    if (isOpen && sessionId) {
      loadSettings();
    }
  }, [isOpen, sessionId]);

  const loadSettings = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/api/poll-settings/session/${sessionId}`);
      setLanguage(response.data.language || 'en');
      setGradientStart(response.data.gradient_start || '#667eea');
      setGradientEnd(response.data.gradient_end || '#764ba2');
    } catch (err) {
      console.error('Error loading poll settings:', err);
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const response = await api.post(`/api/poll-settings/session/${sessionId}`, {
        language,
        gradient_start: gradientStart,
        gradient_end: gradientEnd
      });
      
      console.log('Poll settings saved successfully:', response.data);
      setSaving(false);
      onClose();
    } catch (err) {
      console.error('Error saving poll settings:', err);
      setError('Failed to save settings');
      setSaving(false);
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Poll Display Settings">
      <form onSubmit={handleSubmit} className="space-y-6 p-4">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading settings...</p>
          </div>
        ) : (
          <>
            {/* Language Selection */}
            <div>
              <label htmlFor="languageSelect" className="block text-sm font-medium text-gray-900 mb-2">
                Display Language
              </label>
              <p className="text-sm text-gray-500 mb-3">
                This language will be used for all polls in this session (vote counts, totals, etc.)
              </p>
              <select
                id="languageSelect"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-gray-900"
              >
                <option value="en">English</option>
                <option value="sv">Swedish (Svenska)</option>
              </select>
            </div>

            {/* Gradient Colors */}
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Background Gradient Colors
              </label>
              <p className="text-sm text-gray-500 mb-3">
                These colors will be used for all polls in this session
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="gradientStart" className="block text-xs text-gray-600 mb-1">
                    Start Color
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      id="gradientStart"
                      value={gradientStart}
                      onChange={(e) => setGradientStart(e.target.value)}
                      className="h-10 w-14 border border-gray-300 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={gradientStart}
                      onChange={(e) => setGradientStart(e.target.value)}
                      placeholder="#667eea"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="gradientEnd" className="block text-xs text-gray-600 mb-1">
                    End Color
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      id="gradientEnd"
                      value={gradientEnd}
                      onChange={(e) => setGradientEnd(e.target.value)}
                      className="h-10 w-14 border border-gray-300 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={gradientEnd}
                      onChange={(e) => setGradientEnd(e.target.value)}
                      placeholder="#764ba2"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>
              {/* Gradient Preview */}
              <div 
                className="mt-3 h-12 rounded-lg"
                style={{
                  background: `linear-gradient(135deg, ${gradientStart} 0%, ${gradientEnd} 100%)`
                }}
              />
              <p className="text-xs text-gray-500 mt-1">Preview of gradient background</p>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button
                type="button"
                variant="secondary"
                onClick={handleClose}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Settings'}
              </Button>
            </div>
          </>
        )}
      </form>
    </Modal>
  );
};

export default PollSettingsModal;

